import { memo, useCallback, useEffect, useMemo, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react'
import { useActiveChannelId } from '../../store/index.js'
import {
  asRunApi,
  gapEventsApi,
  computeCsaStats,
  asRunTitle,
  asRunProgramType,
  asRunItemType,
  asRunSpots,
  attestationApi,
  type PurgePreview,
  type AsRunEntry,
  type GapEventEntry,
  type ReportStats,
} from '../../lib/reportsApi.js'
import { programCategoriesApi, antennaElementTypesApi } from '../../lib/api.js'
import type { AntennaElementType, ProgramCategory } from '../../types/domain.js'
import { formatDuration, formatDrift, PROGRAM_TYPE_LABELS, ITEM_TYPE_LABELS } from '../../lib/utils.js'
import { ChevronRight, FileCog, FileSpreadsheet, FileText, RefreshCw, Stamp, Trash2 } from 'lucide-react'
import { PurgeReportsDialog } from './PurgeReportsDialog.js'
import { AttestationTemplateDialog } from './AttestationTemplateDialog.js'
import { PageHeader, PageTabs, type PageTab } from '../common/PageChrome.js'
import { Spinner } from '../common/Spinner.js'
import './ReportsView.css'

/** Traduction des types d'item, source partagee avec la grille et le bandeau. */
function itemTypeLabel(itemType: string): string {
  return ITEM_TYPE_LABELS[itemType as keyof typeof ITEM_TYPE_LABELS] ?? 'Autre'
}

/**
 * Les statuts AsRun sont affiches bruts — c'est le vocabulaire du journal, il
 * doit rester tracable tel quel dans un export. Seule la teinte les distingue.
 */
const STATUS_TONES: Record<string, 'ok' | 'warning' | 'danger' | 'info'> = {
  OK: 'ok',
  GAP_FILLED: 'warning',
  OVERRUN: 'warning',
  ERROR: 'danger',
  MANUAL_OVERRIDE: 'info',
}

const SCENARIO_LABELS: Record<string, string> = {
  FILE_SHORT: 'Fichier trop court',
  FILE_LONG: 'Fichier trop long',
  FILE_FREEZE: 'Freeze image',
  FILE_MISSING: 'Fichier absent',
  FILE_CORRUPT: 'Codec invalide',
  AUDIO_SILENCE: 'Silence audio',
  SIGNAL_LOST: 'Signal perdu',
  INTERLUDE_FAIL: 'Échec interlude',
  SPOT_DRIFT: 'Dérive spot',
}

const REPORT_TABS = [
  { id: 'asrun', label: 'Diffusions passées' },
  { id: 'stats', label: 'Statistiques' },
  { id: 'gaps', label: 'Incidents' },
] as const satisfies readonly PageTab[]

const AS_RUN_HEADINGS = ['', 'Date', 'Programme', 'Type programme', 'Type item', 'Début réel', 'Fin réelle', 'Durée', 'Écart', 'Statut']
const STATS_HEADINGS = ['Type', 'Diffusions', 'Durée réelle', 'Durée prévue', 'Dérive moy.', 'Incidents']
const GAP_HEADINGS = ['Date / Heure', 'Scénario', 'Durée gap', 'Stratégie', 'Résolution']

type Tab = 'asrun' | 'gaps' | 'stats'

export const ReportsView = memo(function ReportsView() {
  const channelId = useActiveChannelId()
  const [tab, setTab] = useState<Tab>('asrun')
  const [from, setFrom] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 7)
    return date.toISOString().slice(0, 10)
  })
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [programTypeFilter, setProgramTypeFilter] = useState('ALL')
  const [includeTechnicalTests, setIncludeTechnicalTests] = useState(true)
  const [asRunLogs, setAsRunLogs] = useState<AsRunEntry[]>([])
  const [gapEvents, setGapEvents] = useState<GapEventEntry[]>([])
  const [reportStats, setReportStats] = useState<ReportStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [purgeOpen, setPurgeOpen] = useState(false)
  // Sert uniquement à alerter l'opérateur qui purge sans avoir rien exporté.
  const [exported, setExported] = useState(false)
  const [purgeNotice, setPurgeNotice] = useState<string | null>(null)
  const [categories, setCategories] = useState<ProgramCategory[]>([])
  // L'as-run enregistre le libellé du type d'élément d'antenne : le filtre doit
  // proposer les types de la chaîne, désormais renommables, pas l'enum.
  const [elementTypes, setElementTypes] = useState<AntennaElementType[]>([])
  // Sélection de programmes (par programId figé) pour l'attestation groupée.
  const [selectedPrograms, setSelectedPrograms] = useState<Set<string>>(() => new Set())
  const [attesting, setAttesting] = useState(false)
  const [templateOpen, setTemplateOpen] = useState(false)

  useEffect(() => {
    if (!channelId) { setCategories([]); setElementTypes([]); return }
    programCategoriesApi.list({ channelId }).then(setCategories).catch(() => setCategories([]))
    antennaElementTypesApi.list({ channelId }).then(setElementTypes).catch(() => setElementTypes([]))
  }, [channelId])

  const reportQuery = useMemo(() => ({
    channelId: channelId ?? undefined,
    from: `${from}T00:00:00Z`,
    to: `${to}T23:59:59Z`,
    limit: 1000,
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    programType: programTypeFilter === 'ALL' ? undefined : programTypeFilter,
    includeTechnicalTests,
  }), [channelId, from, includeTechnicalTests, programTypeFilter, statusFilter, to])

  const load = useCallback(async () => {
    if (!channelId) return
    setLoading(true)
    setError(null)
    try {
      const [logs, stats, gaps] = await Promise.all([
        asRunApi.list(reportQuery),
        asRunApi.stats(reportQuery),
        gapEventsApi.list({ channelId, from: `${from}T00:00:00Z`, to: `${to}T23:59:59Z` }),
      ])
      setAsRunLogs(logs)
      setReportStats(stats)
      setGapEvents(gaps)
      // La sélection d'attestation porte sur le jeu de résultats affiché : un
      // rechargement (période, filtres) la remet à zéro pour éviter d'attester
      // un programme qui n'est plus à l'écran.
      setSelectedPrograms(new Set())
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError))
    } finally {
      setLoading(false)
    }
  }, [channelId, from, reportQuery, to])

  useEffect(() => { load() }, [load])

  const csaStats = useMemo(() => computeCsaStats(asRunLogs), [asRunLogs])

  const handleExportCSV = useCallback(() => {
    if (!asRunLogs.length) return
    const header = 'Date,Debut,Fin,Programme,Type item,Type programme,Duree reelle,Ecart,Statut,Spots diffuses'
    const rows = asRunLogs.map(log => {
      const title = asRunTitle(log) ?? ''
      const start = log.actualStart ? new Date(log.actualStart) : null
      const end = log.actualEnd ? new Date(log.actualEnd) : null
      const actualSec = start && end ? Math.round((end.getTime() - start.getTime()) / 1000) : 0
      const spots = asRunSpots(log)
        .map(spot => spot.advertiserName ? `${spot.spotTitle} (${spot.advertiserName})` : spot.spotTitle)
        .join(' | ')
      return [
        log.scheduledStart ? new Date(log.scheduledStart).toLocaleDateString('fr-FR') : '',
        start ? start.toLocaleTimeString('fr-FR') : '',
        end ? end.toLocaleTimeString('fr-FR') : '',
        `"${title.replace(/"/g, '""')}"`,
        asRunItemType(log),
        asRunProgramType(log) ?? '',
        actualSec,
        log.durationDiffSec ?? '',
        log.status,
        `"${spots.replace(/"/g, '""')}"`,
      ].join(',')
    })
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `rapport_asrun_${from}_${to}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
    setExported(true)
  }, [asRunLogs, from, to])

  const handleExportPDF = useCallback(async () => {
    if (!channelId) return
    try {
      const blob = await asRunApi.downloadPdf({
        channelId,
        from: `${from}T00:00:00Z`,
        to: `${to}T23:59:59Z`,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        programType: programTypeFilter === 'ALL' ? undefined : programTypeFilter,
        includeTechnicalTests,
      })
      const url = URL.createObjectURL(blob)
      const suffix = programTypeFilter === 'ALL' ? 'tous' : programTypeFilter.toLowerCase()
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `rapport_trafficplay_${from}_${to}_${suffix}.pdf`
      anchor.click()
      URL.revokeObjectURL(url)
      setExported(true)
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : String(exportError))
    }
  }, [channelId, from, includeTechnicalTests, programTypeFilter, statusFilter, to])

  const handleToggleProgram = useCallback((programId: string) => {
    setSelectedPrograms(prev => {
      const next = new Set(prev)
      if (next.has(programId)) next.delete(programId)
      else next.add(programId)
      return next
    })
  }, [])

  const handleDownloadAttestation = useCallback(async () => {
    if (!channelId || selectedPrograms.size === 0) return
    setAttesting(true)
    setError(null)
    try {
      const blob = await attestationApi.downloadProgramsPdf({
        channelId,
        programIds: [...selectedPrograms],
        from: `${from}T00:00:00Z`,
        to: `${to}T23:59:59Z`,
      })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `attestation_${from}_${to}.pdf`
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (attestError) {
      setError(attestError instanceof Error ? attestError.message : String(attestError))
    } finally {
      setAttesting(false)
    }
  }, [channelId, from, selectedPrograms, to])

  const handleOpenTemplate = useCallback(() => setTemplateOpen(true), [])
  const handleCloseTemplate = useCallback(() => setTemplateOpen(false), [])

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    load()
  }, [load])
  const handleOpenPurge = useCallback(() => { setPurgeNotice(null); setPurgeOpen(true) }, [])
  const handleClosePurge = useCallback(() => setPurgeOpen(false), [])
  const handlePurged = useCallback((result: { deletedAsRunLogs: number; deletedScheduleItems: number }) => {
    setPurgeOpen(false)
    setPurgeNotice(`Purge effectuée : ${result.deletedAsRunLogs} as-run et ${result.deletedScheduleItems} item(s) de grille supprimés.`)
    load()
  }, [load])
  const handleTabChange = useCallback((nextTab: string) => setTab(nextTab as Tab), [])
  const handleFromChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setFrom(event.target.value), [])
  const handleToChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setTo(event.target.value), [])
  const handleProgramTypeChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => setProgramTypeFilter(event.target.value), [])
  const handleStatusChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => setStatusFilter(event.target.value), [])
  const handleTechnicalTestsChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setIncludeTechnicalTests(event.target.checked), [])

  return (
    <div className="tp-page-shell reports-view">
      <PageHeader
        title="Rapports"
        actions={(
          <>
            <button type="button" className="tp-button" onClick={handleExportCSV} disabled={!asRunLogs.length || loading}>
              <FileSpreadsheet size={14} aria-hidden="true" />
              Exporter CSV
            </button>
            <button type="button" className="tp-button tp-button--primary" onClick={handleExportPDF} disabled={!channelId || loading}>
              <FileText size={14} aria-hidden="true" />
              Exporter PDF
            </button>
            <button
              type="button"
              className="tp-button"
              onClick={handleDownloadAttestation}
              disabled={!channelId || attesting || selectedPrograms.size === 0}
              title={selectedPrograms.size === 0 ? 'Cochez au moins un programme dans « Diffusions passées »' : undefined}
            >
              <Stamp size={14} aria-hidden="true" />
              {attesting ? 'Attestation…' : `Attestation${selectedPrograms.size ? ` (${selectedPrograms.size})` : ''}`}
            </button>
            <button type="button" className="tp-button" onClick={handleOpenTemplate} disabled={!channelId}>
              <FileCog size={14} aria-hidden="true" />
              Modèle
            </button>
            <button type="button" className="tp-button tp-button--danger" onClick={handleOpenPurge} disabled={!channelId || loading}>
              <Trash2 size={14} aria-hidden="true" />
              Purger la période
            </button>
          </>
        )}
      />

      <section className="reports-controls" aria-label="Paramètres et synthèse du rapport">
        <form className="reports-filters" onSubmit={handleSubmit}>
          <Field label="Du">
            <input className="tp-input tp-input--data" type="date" value={from} onChange={handleFromChange} />
          </Field>
          <Field label="Au">
            <input className="tp-input tp-input--data" type="date" value={to} onChange={handleToChange} />
          </Field>
          <Field label="Type de programme">
            <select className="tp-select" value={programTypeFilter} onChange={handleProgramTypeChange}>
              <option value="ALL">Tous</option>
              {categories.map(c => (
                <option key={c.id} value={c.label}>{c.label}</option>
              ))}
              {elementTypes.map(t => (
                <option key={t.id} value={t.label}>{t.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Statut">
            <select className="tp-select" value={statusFilter} onChange={handleStatusChange}>
              <option value="ALL">Tous</option>
              <option value="OK">OK</option>
              <option value="GAP_FILLED">Gap comblé</option>
              <option value="OVERRUN">Dépassement</option>
              <option value="ERROR">Erreur</option>
              <option value="MANUAL_OVERRIDE">Reprise manuelle</option>
            </select>
          </Field>
          <label className="reports-checkbox">
            <input type="checkbox" checked={includeTechnicalTests} onChange={handleTechnicalTestsChange} />
            <span>Inclure les essais techniques</span>
          </label>
          <button type="submit" className="tp-button reports-refresh" disabled={!channelId || loading} data-state={loading ? 'loading' : undefined}>
            <RefreshCw size={13} aria-hidden="true" />
            {loading ? 'Actualisation…' : 'Actualiser'}
          </button>
        </form>

        {error && <div className="tp-feedback tp-feedback--error reports-error" role="alert">{error}</div>}
        {purgeNotice && <div className="tp-feedback tp-feedback--success reports-error" role="status">{purgeNotice}</div>}
        <StatsStrip stats={reportStats} loading={loading} />
      </section>

      <main className="reports-main">
        <div className="reports-tabs-row">
          <PageTabs
            items={REPORT_TABS}
            activeId={tab}
            onChange={handleTabChange}
            ariaLabel="Sections du rapport"
            controlsId="reports-panel"
            idPrefix="reports-tab"
          />
          <div className="reports-count" aria-live="polite">
            {asRunLogs.length} diffusions · {gapEvents.length} incidents
          </div>
        </div>
        <section
          id="reports-panel"
          className="reports-panel"
          role="tabpanel"
          aria-labelledby={`reports-tab-${tab}`}
          aria-busy={loading}
        >
          {tab === 'asrun' && (
            <AsRunTable
              logs={asRunLogs}
              loading={loading}
              selectedPrograms={selectedPrograms}
              onToggleProgram={handleToggleProgram}
            />
          )}
          {tab === 'stats' && <StatsPanel stats={reportStats} csaStats={csaStats} from={from} to={to} />}
          {tab === 'gaps' && <GapEventsTable events={gapEvents} loading={loading} />}
        </section>
      </main>

      {purgeOpen && channelId && (
        <PurgeReportsDialog
          channelId={channelId}
          from={`${from}T00:00:00Z`}
          to={`${to}T23:59:59Z`}
          exported={exported}
          onClose={handleClosePurge}
          onPurged={handlePurged}
        />
      )}

      {templateOpen && channelId && (
        <AttestationTemplateDialog channelId={channelId} onClose={handleCloseTemplate} />
      )}
    </div>
  )
})

const Field = memo(function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="reports-field">
      <span className="tp-field-label">{label}</span>
      {children}
    </label>
  )
})

const StatsStrip = memo(function StatsStrip({ stats, loading }: { stats: ReportStats | null; loading: boolean }) {
  const totals = stats?.totals
  return (
    <div className="reports-summary" aria-label="Synthèse">
      <MiniStat label="Diffusions" value={loading ? '…' : String(totals?.count ?? 0)} />
      <MiniStat label="Durée réelle" value={loading ? '…' : formatDuration(totals?.totalActualSec ?? 0)} />
      <MiniStat label="Durée prévue" value={loading ? '…' : formatDuration(totals?.totalScheduledSec ?? 0)} />
      <MiniStat label="OK" value={loading ? '…' : String(totals?.okCount ?? 0)} tone="success" />
      <MiniStat label="Incidents / écarts" value={loading ? '…' : String(totals?.errorCount ?? 0)} tone={(totals?.errorCount ?? 0) > 0 ? 'danger' : undefined} />
    </div>
  )
})

const MiniStat = memo(function MiniStat({ label, value, tone }: { label: string; value: string; tone?: 'success' | 'danger' }) {
  return (
    <div className={`reports-stat${tone ? ` is-${tone}` : ''}`}>
      <div className="reports-stat__label">{label}</div>
      <div className="reports-stat__value">{value}</div>
    </div>
  )
})

const AsRunTable = memo(function AsRunTable({
  logs, loading, selectedPrograms, onToggleProgram,
}: {
  logs: AsRunEntry[]
  loading: boolean
  selectedPrograms: Set<string>
  onToggleProgram: (programId: string) => void
}) {
  if (loading) return <Empty label="Chargement…" loading />
  return (
    <div className="reports-table-wrap">
      <table className="reports-table">
        <thead>
          <tr>
            {AS_RUN_HEADINGS.map((heading, index) => (
              <th
                key={heading || 'select'}
                scope="col"
                className={index === 0 ? 'reports-table__select-col' : undefined}
                aria-label={index === 0 ? 'Sélection pour attestation' : undefined}
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map(log => (
            <AsRunRow
              key={log.id}
              log={log}
              selected={log.programId ? selectedPrograms.has(log.programId) : false}
              onToggleProgram={onToggleProgram}
            />
          ))}
          {logs.length === 0 && <tr><td colSpan={10}><Empty label="Aucune diffusion sur la période" /></td></tr>}
        </tbody>
      </table>
    </div>
  )
})

const AsRunRow = memo(function AsRunRow({
  log, selected, onToggleProgram,
}: {
  log: AsRunEntry
  selected: boolean
  onToggleProgram: (programId: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const title = asRunTitle(log) ?? '-'
  const programType = asRunProgramType(log) ?? '-'
  const itemType = asRunItemType(log)
  const start = log.actualStart ? new Date(log.actualStart) : null
  const end = log.actualEnd ? new Date(log.actualEnd) : null
  const actualSec = start && end ? Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000)) : 0
  const drift = log.durationDiffSec
  const hasLargeDrift = Boolean(drift && Math.abs(drift) > 30)
  const spots = asRunSpots(log)
  const hasSpots = spots.length > 0

  const toggle = useCallback(() => setExpanded(value => !value), [])
  // Seules les émissions (instantané `programId`) sont attestables : un bloc pub,
  // un direct ou un interlude n'ont pas de fiche programme à certifier ici.
  const programId = log.programId
  const handleSelect = useCallback(() => { if (programId) onToggleProgram(programId) }, [programId, onToggleProgram])

  return (
    <>
      <tr className={hasSpots ? 'reports-table__row is-expandable' : undefined} data-expanded={expanded ? 'true' : undefined}>
        <td className="reports-table__select-col">
          {programId && (
            <input
              type="checkbox"
              checked={selected}
              onChange={handleSelect}
              aria-label={`Sélectionner « ${title} » pour l’attestation`}
            />
          )}
        </td>
        <td>{log.scheduledStart ? new Date(log.scheduledStart).toLocaleDateString('fr-FR') : '-'}</td>
        <td className="reports-table__title" title={title}>
          {hasSpots
            ? (
              <button type="button" className="reports-table__expand" onClick={toggle} aria-expanded={expanded}>
                <ChevronRight size={13} aria-hidden="true" className="reports-table__caret" data-open={expanded ? 'true' : undefined} />
                <span>{title}</span>
              </button>
            )
            : title}
        </td>
        <td>{PROGRAM_TYPE_LABELS[programType as keyof typeof PROGRAM_TYPE_LABELS] ?? programType}</td>
        <td>{itemTypeLabel(itemType)}</td>
        <td>{start ? start.toLocaleTimeString('fr-FR') : '-'}</td>
        <td>{end ? end.toLocaleTimeString('fr-FR') : '-'}</td>
        <td>{formatDuration(actualSec)}</td>
        <td className={hasLargeDrift ? 'reports-table__drift is-warning' : 'reports-table__drift'}>{drift !== null ? formatDrift(drift) : '-'}</td>
        <td><StatusBadge status={log.status} /></td>
      </tr>
      {hasSpots && expanded && (
        <tr className="reports-table__spots-row">
          <td colSpan={10}>
            <ol className="reports-spots">
              {spots.map((spot, index) => (
                <li key={`${spot.order}-${index}`} className="reports-spots__item">
                  <span className="reports-spots__order">{spot.order}</span>
                  <span className="reports-spots__title">{spot.spotTitle}</span>
                  {spot.advertiserName && <span className="reports-spots__advertiser">{spot.advertiserName}</span>}
                  {spot.campaignName && <span className="reports-spots__campaign">{spot.campaignName}</span>}
                  <span className="reports-spots__duration">{formatDuration(spot.durationSec)}</span>
                </li>
              ))}
            </ol>
          </td>
        </tr>
      )}
    </>
  )
})

const StatusBadge = memo(function StatusBadge({ status }: { status: string }) {
  return <span className={`reports-status is-${STATUS_TONES[status] ?? 'warning'}`}>{status}</span>
})

const StatsPanel = memo(function StatsPanel({ stats, csaStats, from, to }: { stats: ReportStats | null; csaStats: ReturnType<typeof computeCsaStats>; from: string; to: string }) {
  const rows = Object.entries(stats?.byProgramType ?? {}).sort(([, first], [, second]) => second.actualSec - first.actualSec)
  return (
    <div className="reports-stats-panel">
      <p className="reports-period">
        Période : {new Date(from).toLocaleDateString('fr-FR')} – {new Date(to).toLocaleDateString('fr-FR')}
      </p>
      <div className="reports-stats-grid">
        <MiniStat label="Temps antenne" value={formatDuration(stats?.totals.totalActualSec ?? 0)} />
        <MiniStat label="Dérive moyenne" value={formatDuration(stats?.totals.avgAbsDriftSec ?? 0)} tone={(stats?.totals.avgAbsDriftSec ?? 0) > 30 ? 'danger' : undefined} />
        <MiniStat label="Taux couverture 24 h" value={`${csaStats.coveragePct}%`} />
        <MiniStat label="Types diffusés" value={String(rows.length)} />
      </div>
      <h2 className="reports-section-title">Répartition par type de programme</h2>
      <div className="reports-table-wrap reports-table-wrap--stats">
        <table className="reports-table">
          <thead><tr>{STATS_HEADINGS.map(heading => <th key={heading} scope="col">{heading}</th>)}</tr></thead>
          <tbody>
            {rows.map(([type, value]) => (
              <tr key={type}>
                <td>{PROGRAM_TYPE_LABELS[type as keyof typeof PROGRAM_TYPE_LABELS] ?? type}</td>
                <td>{value.count}</td>
                <td>{formatDuration(value.actualSec)}</td>
                <td>{formatDuration(value.scheduledSec)}</td>
                <td>{formatDuration(value.avgAbsDriftSec)}</td>
                <td>{value.errorCount}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6}><Empty label="Aucune statistique sur la période" /></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
})

const GapEventsTable = memo(function GapEventsTable({ events, loading }: { events: GapEventEntry[]; loading: boolean }) {
  if (loading) return <Empty label="Chargement…" loading />
  return (
    <div className="reports-table-wrap">
      <table className="reports-table">
        <thead><tr>{GAP_HEADINGS.map(heading => <th key={heading} scope="col">{heading}</th>)}</tr></thead>
        <tbody>
          {events.map(event => (
            <tr key={event.id}>
              <td>{new Date(event.triggeredAt).toLocaleString('fr-FR')}</td>
              <td>{SCENARIO_LABELS[event.scenario] ?? event.scenario}</td>
              <td>{formatDuration(event.gapSec)}</td>
              <td>{event.strategy}</td>
              <td><span className={`reports-status${event.resolvedAt ? ' is-ok' : ' is-warning'}`}>{event.resolvedAt ? 'Résolue' : 'En cours'}</span></td>
            </tr>
          ))}
          {events.length === 0 && <tr><td colSpan={5}><Empty label="Aucun incident sur la période" /></td></tr>}
        </tbody>
      </table>
    </div>
  )
})

const Empty = memo(function Empty({ label, loading }: { label: string; loading?: boolean }) {
  return (
    <div className="tp-empty-state reports-empty">
      {loading && <Spinner />} {label}
    </div>
  )
})
